import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUsers, updateUser } from '../api/api';
import { Shield, Check, X, User, Save, Eye, BarChart2, Wallet, DollarSign, PieChart } from 'lucide-react';

const Permissions = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const permissionsList = [
    { key: 'view_sales_report', label: 'Ver Reporte de Ventas', icon: BarChart2, description: 'Permite ver las ventas por método de pago' },
    { key: 'view_expenses_report', label: 'Ver Reporte de Gastos', icon: DollarSign, description: 'Permite ver el historial de gastos' },
    { key: 'view_charts', label: 'Ver Gráficas', icon: PieChart, description: 'Permite ver gráficas y estadísticas visuales' },
    { key: 'view_petty_cash', label: 'Ver Fondo Sencillo', icon: Wallet, description: 'Permite ver información del fondo de sencillo' },
    { key: 'view_monthly_summary', label: 'Ver Resumen Mensual', icon: Eye, description: 'Permite ver el resumen mensual de la tienda' },
    { key: 'view_real_balance', label: 'Ver Saldo Real', icon: DollarSign, description: 'Permite ver el saldo disponible real' },
  ];

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      const filteredUsers = data.filter(u => u.role !== 'admin' && u.is_active);
      setUsers(filteredUsers);
    } catch (err) {
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handlePermissionChange = (userId, permissionKey, value) => {
    setUsers(prevUsers => 
      prevUsers.map(u => {
        if (u.id === userId) {
          return {
            ...u,
            permissions: {
              ...u.permissions,
              [permissionKey]: value
            }
          };
        }
        return u;
      })
    );
  };

  const saveUserPermissions = async (userId) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const userToUpdate = users.find(u => u.id === userId);
      await updateUser(userId, { permissions: userToUpdate.permissions });
      setSuccess('Permisos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Error al guardar permisos');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const toggleAllPermissions = (userId, value) => {
    setUsers(prevUsers =>
      prevUsers.map(u => {
        if (u.id === userId) {
          const newPermissions = {};
          permissionsList.forEach(p => {
            newPermissions[p.key] = value;
          });
          return { ...u, permissions: newPermissions };
        }
        return u;
      })
    );
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'manager': 'bg-purple-100 text-purple-800',
      'supervisor': 'bg-blue-100 text-blue-800',
      'store_manager': 'bg-green-100 text-green-800',
      'cashier': 'bg-yellow-100 text-yellow-800',
      'purchase_manager': 'bg-orange-100 text-orange-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'manager': 'Gerente',
      'supervisor': 'Supervisor',
      'store_manager': 'Encargado',
      'cashier': 'Cajero',
      'purchase_manager': 'Enc. Compras',
    };
    return labels[role] || role;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <Shield size={48} className="mx-auto text-red-500 mb-4" />
        <p className="text-lg text-gray-600">No tienes permisos para acceder a esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl shadow-xl p-6 text-white border border-slate-600/30">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 rounded-lg p-2.5">
            <Shield size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Gestión de Permisos</h1>
            <p className="text-slate-300 text-sm">Controla qué puede ver cada usuario en su dashboard</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center space-x-2">
          <Check size={20} />
          <span>{success}</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
          <X size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Permisos Disponibles:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {permissionsList.map(perm => {
            const Icon = perm.icon;
            return (
              <div key={perm.key} className="flex items-center space-x-2 text-sm">
                <Icon size={16} className="text-slate-500" />
                <span className="text-slate-700">{perm.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-lg">
          <User size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No hay usuarios para configurar</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-slate-700 to-blue-700 text-white">
                  <th className="px-4 py-3 text-left text-sm font-bold uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-sm font-bold uppercase">Rol</th>
                  {permissionsList.map(perm => (
                    <th key={perm.key} className="px-3 py-3 text-center text-xs font-bold uppercase" title={perm.description}>
                      {perm.label.split(' ').slice(-1)[0]}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-sm font-bold uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((userItem, index) => (
                  <tr key={userItem.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-slate-800">{userItem.name}</p>
                        <p className="text-xs text-slate-500">@{userItem.username}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${getRoleBadgeColor(userItem.role)}`}>
                        {getRoleLabel(userItem.role)}
                      </span>
                    </td>
                    {permissionsList.map(perm => (
                      <td key={perm.key} className="px-3 py-3 text-center">
                        <button
                          onClick={() => handlePermissionChange(userItem.id, perm.key, !userItem.permissions?.[perm.key])}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            userItem.permissions?.[perm.key] !== false
                              ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}
                        >
                          {userItem.permissions?.[perm.key] !== false ? <Check size={18} /> : <X size={18} />}
                        </button>
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => toggleAllPermissions(userItem.id, true)}
                          className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                          title="Activar todos"
                        >
                          Todos
                        </button>
                        <button
                          onClick={() => toggleAllPermissions(userItem.id, false)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          title="Desactivar todos"
                        >
                          Ninguno
                        </button>
                        <button
                          onClick={() => saveUserPermissions(userItem.id)}
                          disabled={saving[userItem.id]}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
                        >
                          {saving[userItem.id] ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <Save size={14} />
                          )}
                          <span className="text-xs">Guardar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h3 className="font-bold text-blue-800 mb-2">💡 Cómo funciona:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Los <strong>Administradores</strong> siempre tienen todos los permisos.</li>
          <li>• Haz clic en el ✓ o ✗ para cambiar un permiso individual.</li>
          <li>• Usa "Todos" o "Ninguno" para cambios rápidos.</li>
          <li>• No olvides hacer clic en <strong>Guardar</strong> para aplicar los cambios.</li>
        </ul>
      </div>
    </div>
  );
};

export default Permissions;
